import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// A dedicated axios instance with credentials enabled. The previous version
// of this file used the bare `axios` module directly, which does NOT send
// the httponly session_token cookie cross-origin. Every one of these
// endpoints requires an authenticated user (they look up the order by
// user_id), so without withCredentials every call here would have failed
// auth with a 401 as soon as the backend actually checked for a session -
// which it now does (see backend/payments/controller.py).
const paymentClient = axios.create({
  baseURL: `${API_URL}/api/payments`,
  withCredentials: true,
});

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

// Echoes csrf_token cookie back as a header on state-changing requests -
// required now that the backend enforces double-submit CSRF checking on
// every authenticated POST (see server.py's csrf_protection middleware).
paymentClient.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCookie('csrf_token');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

export const paymentAPI = {
  // Get available payment methods
  getMethods: async () => {
    const response = await paymentClient.get('/methods');
    return response.data;
  },

  // M-Pesa (via Pesapal - returns a hosted checkout redirect_url, not an STK push)
  initiateMpesa: async (phoneNumber, amount, orderId, email = null) => {
    const response = await paymentClient.post('/mpesa/initiate', {
      phone_number: phoneNumber,
      amount,
      order_id: orderId,
      email,
      currency: 'KES'
    });
    return response.data;
  },

  getMpesaStatus: async (orderTrackingId) => {
    const response = await paymentClient.get(`/mpesa/status/${orderTrackingId}`);
    return response.data;
  },

  // Stripe
  createStripeSession: async (orderId, amount, currency = 'usd', customerEmail = null) => {
    const params = new URLSearchParams({
      order_id: orderId,
      amount: amount.toString(),
      currency
    });
    if (customerEmail) params.append('customer_email', customerEmail);

    const response = await paymentClient.post(`/stripe/create-session?${params}`);
    return response.data;
  },

  getStripeSession: async (sessionId) => {
    const response = await paymentClient.get(`/stripe/session/${sessionId}`);
    return response.data;
  },

  // PayPal
  createPayPalOrder: async (orderId, amount, currency = 'USD') => {
    const params = new URLSearchParams({
      order_id: orderId,
      amount: amount.toString(),
      currency
    });
    const response = await paymentClient.post(`/paypal/create-order?${params}`);
    return response.data;
  },

  capturePayPalOrder: async (paypalOrderId) => {
    const response = await paymentClient.post(`/paypal/capture/${paypalOrderId}`);
    return response.data;
  },

  getPayPalOrder: async (paypalOrderId) => {
    const response = await paymentClient.get(`/paypal/order/${paypalOrderId}`);
    return response.data;
  },

  // Skrill
  createSkrillSession: async (orderId, amount, currency = 'USD', customerEmail = null) => {
    const params = new URLSearchParams({
      order_id: orderId,
      amount: amount.toString(),
      currency
    });
    if (customerEmail) params.append('customer_email', customerEmail);

    const response = await paymentClient.post(`/skrill/create-session?${params}`);
    return response.data;
  },

  // Generic status
  getPaymentStatus: async (transactionId) => {
    const response = await paymentClient.get(`/status/${transactionId}`);
    return response.data;
  }
};

export default paymentAPI;
